import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PHC_DEFAULT_THEME,
  PHC_THEME_STORAGE_KEY,
  PHC_THEMES,
  SNAPSHOT_THEMES,
} from "./phcThemes";

const PhcThemeContext = createContext({
  mode: PHC_DEFAULT_THEME,
  theme: PHC_THEMES[PHC_DEFAULT_THEME],
  snapshotThemes: SNAPSHOT_THEMES[PHC_DEFAULT_THEME],
  setMode: () => {},
  toggle: () => {},
});

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(PHC_THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore storage errors
  }
  return PHC_DEFAULT_THEME;
};

export function PhcThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(PHC_THEME_STORAGE_KEY, mode);
    } catch {
      // ignore storage errors
    }
  }, [mode]);

  const setMode = useCallback((next) => {
    if (next === "light" || next === "dark") {
      setModeState(next);
    }
  }, []);

  const toggle = useCallback(() => {
    setModeState((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      theme: PHC_THEMES[mode] || PHC_THEMES.light,
      snapshotThemes: SNAPSHOT_THEMES[mode] || SNAPSHOT_THEMES.light,
      setMode,
      toggle,
    }),
    [mode, setMode, toggle],
  );

  return (
    <PhcThemeContext.Provider value={value}>{children}</PhcThemeContext.Provider>
  );
}

export const usePhcTheme = () => useContext(PhcThemeContext);
